import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert, Box, Button, IconButton, LinearProgress, MenuItem, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material'
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { useSnackbar } from 'notistack'
import StatusChip from '../components/StatusChip'
import RequestDetailDrawer from '../components/RequestDetailDrawer'
import { fetchMyRequests, fetchDownloadUrl, extractPiiError } from '../api'
import { PROCESSING_POLL_MS, STATUS_FILTER_OPTIONS, STATUS_META } from '../constants'
import { formatDate1 } from '../../../common/util'

/**
 * Screen 2 — My Requests (BRD §7.3): paginated, newest first, status filter,
 * auto-refresh while any row is PROCESSING, Download gated to COMPLETED + unexpired.
 */
export default function MyRequestsTab({ isL2, refreshKey }) {
  const { enqueueSnackbar } = useSnackbar()
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0) // MUI 0-indexed; API is 1-indexed
  const [size, setSize] = useState(20)
  const [status, setStatus] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [detailId, setDetailId] = useState(null)

  const load = useCallback((quiet) => {
    if (!quiet) setLoading(true)
    fetchMyRequests({
      status: status || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      page: page + 1,
      size,
    })
      .then((res) => {
        setRows(res.data?.items || [])
        setTotal(res.data?.totalRecords || 0)
        setError(null)
      })
      .catch((err) => setError(extractPiiError(err, 'Failed to load your requests')))
      .finally(() => { if (!quiet) setLoading(false) })
  }, [status, fromDate, toDate, page, size])

  useEffect(() => { load() }, [load, refreshKey])

  // Quiet auto-refresh while a file is being generated
  useEffect(() => {
    if (!rows.some((r) => r.status === 'PROCESSING')) return undefined
    const t = setInterval(() => load(true), PROCESSING_POLL_MS)
    return () => clearInterval(t)
  }, [rows, load])

  const download = (r) => {
    fetchDownloadUrl(r.requestId)
      .then((res) => {
        const url = res.data?.downloadUrl
        if (url) window.open(url, '_blank', 'noopener')
      })
      .catch((err) => enqueueSnackbar(extractPiiError(err, 'Download failed'), { variant: 'error' }))
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <TextField
          select size="small" label="Status" value={status} sx={{ minWidth: 180 }}
          onChange={(e) => { setStatus(e.target.value); setPage(0) }}
        >
          <MenuItem value="">All</MenuItem>
          {STATUS_FILTER_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>{STATUS_META[s].label}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small" type="date" label="From" value={fromDate}
          InputLabelProps={{ shrink: true }}
          onChange={(e) => { setFromDate(e.target.value); setPage(0) }}
        />
        <TextField
          size="small" type="date" label="To" value={toDate}
          InputLabelProps={{ shrink: true }}
          onChange={(e) => { setToDate(e.target.value); setPage(0) }}
        />
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={() => load()} title="Refresh"><RefreshOutlinedIcon /></IconButton>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 1 }} />}
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Request ID</TableCell>
              <TableCell>Submitted on</TableCell>
              <TableCell align="right">AWB count</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.requestId} hover>
                <TableCell>
                  <Button size="small" onClick={() => setDetailId(r.requestId)}>{r.requestId}</Button>
                </TableCell>
                <TableCell>{r.submittedOn ? formatDate1(r.submittedOn) : '—'}</TableCell>
                <TableCell align="right">{r.awbCount != null ? r.awbCount : '—'}</TableCell>
                <TableCell sx={{ maxWidth: 260 }}>
                  <Tooltip title={r.reason || ''}>
                    <Typography variant="body2" noWrap>{r.reason}</Typography>
                  </Tooltip>
                </TableCell>
                <TableCell><StatusChip status={r.status} /></TableCell>
                <TableCell align="right">
                  {r.status === 'COMPLETED' && (
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                      <Tooltip title={r.expiresOn ? `Expires ${formatDate1(r.expiresOn)}` : 'Download output CSV'}>
                        <IconButton size="small" color="primary" onClick={() => download(r)}>
                          <DownloadOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                  {r.status === 'EXPIRED' && (
                    <Tooltip title={`File expired${r.expiresOn ? ` on ${formatDate1(r.expiresOn)}` : ''}. Raise a new request.`}>
                      <span>
                        <IconButton size="small" disabled><DownloadOutlinedIcon fontSize="small" /></IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {(r.status === 'REJECTED_L1' || r.status === 'REJECTED_L2') && (
                    <Button size="small" onClick={() => setDetailId(r.requestId)}>View remark</Button>
                  )}
                  {!['COMPLETED', 'EXPIRED', 'REJECTED_L1', 'REJECTED_L2'].includes(r.status) && (
                    <IconButton size="small" onClick={() => setDetailId(r.requestId)} title="View detail">
                      <VisibilityOutlinedIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                  No requests yet — create your first request from the New Request tab.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        rowsPerPage={size}
        rowsPerPageOptions={[10, 20, 50]}
        onPageChange={(e, p) => setPage(p)}
        onRowsPerPageChange={(e) => { setSize(parseInt(e.target.value, 10)); setPage(0) }}
      />

      <RequestDetailDrawer
        requestId={detailId}
        open={!!detailId}
        onClose={() => setDetailId(null)}
        isL2={isL2}
        onChanged={() => load()}
      />
    </Box>
  )
}
